import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircleHelp, Target, Clock } from 'lucide-react';
import RewardAnimationVideo from '@/components/ui/RewardAnimationVideo';

interface ReviewResultProps {
  correctAnswers?: number;
  totalQuestions?: number;
  timeSpent?: string; // mm:ss format
  accuracy?: number;
}

const ReviewResult: React.FC<ReviewResultProps> = ({
  correctAnswers = 5,
  totalQuestions = 8,
  timeSpent = '05:30',
  accuracy = 85
}) => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const [showStats, setShowStats] = useState(false);
  const [motivation, setMotivation] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);

  const state = location?.state || {};
  const dynamicAccuracy = state?.accuracy_percentage ?? accuracy;
  const dynamicTimeSpent = state?.total_time_spent ?? timeSpent;
  
  // Use server-provided total questions if available
  const calculatedTotalQuestions = useMemo(() => {
    return state?.total_questions ?? totalQuestions;
  }, [state?.total_questions, totalQuestions]);

  // Check if there were any questions
  const hasQuestions = calculatedTotalQuestions > 0;

  const toPersianDigits = (s: string) => s.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
  const toPersianNumber = (n: number | string) => toPersianDigits(String(n));

  // Display time as mm:ss (Persian digits)
  const displayTime = useMemo(() => {
    const timeStr = dynamicTimeSpent || timeSpent;
    return toPersianDigits(timeStr);
  }, [dynamicTimeSpent, timeSpent]);

  useEffect(() => {
    // Show stats after character animation
    const timer = setTimeout(() => {
      setShowStats(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setMotivation('عالی.همین جوری برو جلو');
  }, []);

  const handleFinishReview = async () => {
    if (isNavigating) return; // guard multiple clicks
    setIsNavigating(true);
    
    // Get bookId from localStorage (saved in LearningPath)
    const lastLearningPathBookId = localStorage.getItem('lastLearningPathBookId');
    const bookId = lastLearningPathBookId ? parseInt(lastLearningPathBookId, 10) : null;
    
    if (bookId && bookId > 0) {
      navigate(`/learning/path`, { state: { bookId }, replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  // Compute dynamic labels for Accuracy and Time
  const accuracyLabel = useMemo(() => {
    const a = Number(dynamicAccuracy);
    if (a > 70) return 'عالی';
    if (a >= 50) return 'خوب';
    if (a >= 30) return 'متوسط';
    return 'دقت پایین';
  }, [dynamicAccuracy]);

  const timeMinutes = useMemo(() => {
    const timeStr = dynamicTimeSpent || timeSpent;
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      const pm = parseInt(parts[0], 10);
      const ps = parseInt(parts[1], 10);
      if (!Number.isNaN(pm) && !Number.isNaN(ps)) {
        return pm + ps / 60;
      }
    }
    return 5; // fallback
  }, [dynamicTimeSpent, timeSpent]);

  const timeLabel = useMemo(() => {
    if (timeMinutes < 1) return 'سریع';
    if (timeMinutes <= 2) return 'کمی سریع';
    return 'کند';
  }, [timeMinutes]);

  return (
    <div className="min-h-screen bg-white text-gray-800 flex flex-col items-center justify-center px-4 overflow-x-hidden" dir="rtl">
      {/* Character Animation */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
        className="relative mb-6 w-full max-w-[300px] h-[200px] flex items-center justify-center overflow-hidden"
      >
        <RewardAnimationVideo size={200} />
      </motion.div>

      {/* Success Message */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="text-center mb-8"
      >
        <h1 className="text-4xl font-bold text-[#7163ff] mb-4 font-persian">
          آفرین! مرور تموم شد
        </h1>
        <p className="text-gray-700 text-lg font-persian leading-relaxed">{motivation}</p>
      </motion.div>

      {/* Stats Cards - Only show if there were questions */}
      {hasQuestions && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: showStats ? 1 : 0, y: showStats ? 0 : 40 }}
          transition={{ duration: 0.6 }}
          className="flex justify-between gap-3 mb-8 w-full max-w-[320px]"
        >
          {/* Total Questions */}
          <div className="bg-white rounded-2xl shadow-sm flex flex-col aspect-square overflow-hidden flex-1">
            <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 px-2 py-1.5">
              <div className="text-white text-[11px] font-bold font-persian text-center">تعداد سوال</div>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-2">
              <div className="flex flex-row-reverse items-center justify-center gap-1.5">
                <CircleHelp className="w-4 h-4 text-yellow-500" />
                <div className="text-lg font-bold text-yellow-600 font-persian">{toPersianNumber(calculatedTotalQuestions)}</div>
              </div>
            </div>
          </div>

          {/* Accuracy */}
          <div className="bg-white rounded-2xl shadow-sm flex flex-col aspect-square overflow-hidden flex-1">
            <div className="bg-gradient-to-r from-green-400 to-green-500 px-2 py-1.5">
              <div className="text-white text-[11px] font-bold font-persian text-center">{accuracyLabel}</div>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-2">
              <div className="flex flex-row-reverse items-center justify-center gap-1.5">
                <Target className="w-4 h-4 text-green-500" />
                <div className="text-lg font-bold text-green-600 font-persian">{toPersianNumber(dynamicAccuracy)}٪</div>
              </div>
            </div>
          </div>

          {/* Time Spent */}
          <div className="bg-white rounded-2xl shadow-sm flex flex-col aspect-square overflow-hidden flex-1">
            <div className="bg-gradient-to-r from-blue-400 to-blue-500 px-2 py-1.5">
              <div className="text-white text-[11px] font-bold font-persian text-center">{timeLabel}</div>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center p-2">
              <div className="flex flex-row-reverse items-center justify-center gap-1.5">
                <Clock className="w-4 h-4 text-blue-500" />
                <div className="text-lg font-bold text-blue-600 font-persian">{displayTime}</div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Finish Review Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: showStats ? 1 : 0, scale: showStats ? 1 : 0.8 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleFinishReview}
        disabled={isNavigating}
        className={`w-[82%] max-w-[340px] py-3.5 rounded-xl text-center font-bold font-persian text-[15px] transition-all duration-300 active:scale-95 shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 ${isNavigating ? 'bg-[#a7a0ff] cursor-not-allowed' : 'bg-[#6e61ff] text-white hover:bg-[#5a4ed9]'}`}
      >
        {isNavigating ? (
          <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : (
          <span>اتمام مرور</span>
        )}
      </motion.button>

    </div>
  );
};

export default ReviewResult;
